import { first } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HotelListingComponent } from '../hotel-listing/hotel-listing.component';
import { Component, Input, OnInit } from '@angular/core';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { HotelListingService } from '../hotel-listing.service';
import { HotelListing } from '../hotel-listing';
import { PropertyService } from '@app/_components/property/property.service';

/**
 * Hotel Room V2 — list / table component for str_hotel_listing rows under
 * a single hotel-room parent STR. Sibling of CompositeListingListComponent.
 *
 * Used in two places:
 *   1. Property-edit "Children" tab when listingType === 3 (hotel-room) — embeds with
 *      the parent's shortTermRentalId so only that hotel-room's children are shown.
 *   2. Standalone admin route /hotel-listings (future use, mirrors /composite-listings).
 */
@Component({
    selector: 'app-hotel-listing-list',
    templateUrl: './hotel-listing-list.component.html',
    styleUrls: ['./hotel-listing-list.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class HotelListingListComponent implements OnInit {
	properties: any;
	sitePriorities: any;

	constructor(
		private hotelListingService: HotelListingService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private propertyService: PropertyService,
		private dialog: MatDialog,
	) {
		this.statuses = hotelListingService.statuses;
		this.sitePriorities = hotelListingService.sitePriorities;
	}

	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	/** Hotel-room parent STR id — when set, the list filters to children of this hotel-room only. */
	@Input() shortTermRentalId: any;

	hotelListings: any;
	hotelListing: HotelListing;

	first = 0;
	rows = 50;

	datasource: any;
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];
	formLoading = false;
	statuses: any;

	columns: any[] = [
		{ field: 'hotelListingId',           header: 'Id',                       width: 'auto' },
		{ field: 'hotelShortTermRentalId',   header: 'Hotel Room Property Code', width: 'auto' },
		{ field: 'shortTermRentalId',        header: 'Child Property Code',      width: 'auto' },
		{ field: 'listingPriority',          header: 'Listing Priority',         width: 'auto' },
		{ field: 'ruSubUnitId',              header: 'RU Sub-Unit ID',           width: 'auto' },
	];

	ngOnInit(): void {
		this.fetchProperties();
		try {
			this.multiSortMeta = [];
			this.multiSortMeta.push({ field: 'createDate', order: 0 });
		}
		catch (error) {
			console.log('Error: ' + error);
		}
	}

	fetchProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.propertySummaryForShortData()
					.subscribe(
						res => {
							this.properties = res.data.properties;
							return this.properties;
						},
						err => {
							console.log('hotelListing fetch-properties FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.alertService.error(error);
			}
		}
		else {
			return (this.properties);
		}
	}

	onRowDelete(event: any) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + event.hotelListingId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete(event);
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			this.lazyLoadEvent = event;

			if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			if (this.shortTermRentalId) {
				// scope the list to children of this hotel-room parent
				const parentFilter = { 'hotelShortTermRentalId': { 'value': this.shortTermRentalId, 'matchMode': 'in' } };
				Object.assign(event.filters, parentFilter);
			}

			const pageNumber = Math.round(event.first / event.rows);
			this.hotelListingService.findByFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(
					(res: any) => {
						this.recordTotal = res.page.recordTotal;
						this.hotelListings = res.data.hotelListings;
					},
					(err: any) => {
						console.log('hotelListings list error: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.loading = false; }
				);

			if (this.datasource) {
				this.hotelListings = this.datasource.slice(event.first, (event.first + event.rows));
			}
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}

	onRowSelect(event: any) {
		if (this.shortTermRentalId) {
			this.onOpenFormEdit(event.data);
		}
	}

	formAdd() { }

	formUpdate(hotelListing: HotelListing) {
		try {
			this.formLoading = true;
			this.hotelListingService.update(hotelListing)
				.subscribe(
					(res: any) => {
						this.hotelListings.map((existing, i) => {
							if (existing.hotelListingId === res.data.hotelListing.hotelListingId) {
								this.hotelListings[i] = res.data.hotelListing;
							}
						});
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
					},
					(err: any) => {
						console.log('hotelListing-update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formDelete(hotelListing: any) {
		try {
			this.formLoading = true;
			this.hotelListingService.delete(hotelListing.hotelListingId)
				.subscribe(
					(res: any) => {
						this.hotelListings.splice(this.hotelListings.indexOf(hotelListing), 1);
						this.recordTotal = this.hotelListings.length;
						this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'HotelListing ' + hotelListing.hotelListingId + ' Deleted', life: 3000 });
					},
					(err: any) => {
						console.log('hotelListing-delete FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'HotelListing ' + hotelListing.hotelListingId + ' could not be deleted.', life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	onOpenFormAdd() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.data = {
			title: 'Add Hotel Room Child',
			hotelShortTermRentalId: this.shortTermRentalId,
			// Pass current children so the dialog can guard against duplicate adds.
			existingChildIds: (this.hotelListings || []).map((h: any) => h.shortTermRentalId)
		};
		const dialogRef = this.dialog.open(HotelListingComponent, dialogConfig);
		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				this.addHotelListingCB(data);
				dialogRef.close();
			});
	}

	onOpenFormEdit(hotelListing: HotelListing) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.data = {
			hotelListing: hotelListing,
			title: 'Edit Hotel Room Child'
		};
		const dialogRef = this.dialog.open(HotelListingComponent, dialogConfig);
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				this.updateHotelListingCB(data);
				dialogRef.close();
			});
		dialogRef.componentInstance.doDelete.subscribe(
			data => {
				this.deleteHotelListingCB(data);
				dialogRef.close();
			});
	}

	addHotelListingCB(hotelListing: HotelListing) {
		if (!this.hotelListings) {
			this.hotelListings = [];
		}
		this.hotelListings.unshift(hotelListing);
		this.hotelListings = [...this.hotelListings];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'HotelListing ' + hotelListing.hotelListingId + ' Added', life: 3000 });
	}

	updateHotelListingCB(hotelListing: HotelListing) {
		const itemIndex = this.hotelListings.findIndex((item: any) => item.hotelListingId === hotelListing.hotelListingId);
		this.hotelListings[itemIndex] = hotelListing;
		this.hotelListings = [...this.hotelListings];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'HotelListing ' + hotelListing.hotelListingId + ' Updated', life: 3000 });
	}

	deleteHotelListingCB(hotelListing: HotelListing) {
		this.hotelListings.splice(this.hotelListings.indexOf(hotelListing), 1);
		this.recordTotal = this.hotelListings.length;
		this.hotelListings = [...this.hotelListings];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'HotelListing ' + hotelListing.hotelListingId + ' Deleted', life: 3000 });
	}

	/**
	 * Hotel Room V2 (Phase 2) — admin re-bind RU sub-unit IDs (UnitId / ResApaID)
	 * to this hotel-room's children. Used after the property is created on RU's
	 * side so the bookings-to-child mapping is established before the first OTA
	 * booking arrives.
	 */
	onRebindRuSubUnits() {
		if (!this.shortTermRentalId) {
			this.messageService.add({ severity: 'warn', summary: 'No parent selected', detail: 'Cannot rebind without a hotel-room parent context.', life: 4000 });
			return;
		}
		this.hotelListingService.rebindRuSubUnits(this.shortTermRentalId)
			.subscribe(
				(res: any) => {
					const bound = res?.data?.rebindResult?.newlyBoundSlotCount ?? 0;
					this.messageService.add({ severity: 'success', summary: 'RU sub-unit rebind complete', detail: bound + ' new slot mapping(s) bound. Already-mapped slots preserved.', life: 5000 });
				},
				(err: any) => {
					console.log('rebindRuSubUnits FAIL: ' + JSON.stringify(err));
					this.messageService.add({ severity: 'error', summary: 'Rebind failed', detail: JSON.stringify(err), life: 5000 });
				});
	}

	next() { this.first = this.first + this.rows; }
	prev() { this.first = this.first - this.rows; }
	reset() { this.first = 0; }
	isLastPage(): boolean { return this.hotelListings ? this.first === (this.hotelListings.length - this.rows) : true; }
	isFirstPage(): boolean { return this.hotelListings ? this.first === 0 : true; }

	get Statuses() {
		return this.statuses;
	}

	getPropertyCode(shortTermRentalId: any) {
		if (shortTermRentalId) {
			const status = this.properties.find((e: any) => e.shortTermRentalId == shortTermRentalId);
			return status?.propertyCode ?? '';
		}
		return '';
	}

	reloadPage() {
		window.location.reload();
	}

	showPaginator() {
		return true;
	}
}
